import { useState, useRef, useEffect, useId } from "react";
import Reveal from "../components/Reveal";
import { IconStar, IconArrow, IconCheck, IconInstagram, IconClose } from "../components/icons";
import heroGallery from "../assets/images/hero/hero-gallery.png";

const MAX_FILES = 5;
const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10MB per file (Netlify Forms is sensitive to large payloads)
const ACCEPTED_PREFIXES = ["image/", "video/"];

const formatBytes = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const ROW = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 190px), 1fr))",
  gap: "16px",
};

const SR_ONLY = {
  position: "absolute", width: "1px", height: "1px",
  padding: 0, margin: "-1px", overflow: "hidden",
  clip: "rect(0,0,0,0)", whiteSpace: "nowrap", borderWidth: 0,
};

const EMPTY_FORM = {
  name: "",
  email: "",
  rating: 0,
  review: "",
  consent: false,
};

function StarRating({ value, hover, onSet, onHover, onLeave }) {
  return (
    <div
      role="radiogroup"
      aria-label="Overall rating"
      onMouseLeave={onLeave}
      style={{ display: "flex", gap: "6px", alignItems: "center" }}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const active = n <= (hover || value);
        return (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={`${n} star${n === 1 ? "" : "s"}`}
            onClick={() => onSet(n)}
            onMouseEnter={() => onHover(n)}
            onFocus={() => onHover(n)}
            onBlur={onLeave}
            style={{
              background: "none", border: "none", cursor: "pointer",
              padding: "4px", lineHeight: 0,
              color: active ? "var(--gold)" : "#D8CCDD",
              transform: active ? "scale(1.1)" : "scale(1)",
              transition: "transform 0.2s ease, color 0.2s ease",
            }}
          >
            <span style={{ display: "inline-block", transform: "scale(1.8)" }}>
              <IconStar />
            </span>
          </button>
        );
      })}
      <span style={{
        marginLeft: "10px", fontSize: "0.88rem", color: "var(--text-secondary)",
        minWidth: "80px",
      }}>
        {(hover || value)
          ? ["", "Could be better", "It was okay", "Good", "Great", "Loved it!"][hover || value]
          : "Tap to rate"}
      </span>
    </div>
  );
}

export default function LeaveReview() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [hoverRating, setHoverRating] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [attachments, setAttachments] = useState([]); // { file, url, kind }
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const fileInputId = useId();

  // Stable ref so the unmount cleanup can see the latest attachments
  // without re-running on every change (which would revoke URLs still in use).
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;
  useEffect(() => {
    return () => {
      attachmentsRef.current.forEach((a) => URL.revokeObjectURL(a.url));
    };
  }, []);

  const handleChange = (field) => (e) =>
    setForm((prev) => ({
      ...prev,
      [field]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const isAcceptedType = (file) =>
    ACCEPTED_PREFIXES.some((p) => file.type.startsWith(p));

  const showError = (msg) => {
    setError(true);
    setErrorMsg(msg);
  };

  const addFiles = (incoming) => {
    if (!incoming || incoming.length === 0) return;

    const remainingSlots = MAX_FILES - attachments.length;
    if (remainingSlots <= 0) {
      showError(`You can upload up to ${MAX_FILES} files. Remove one to add another.`);
      return;
    }

    const rejected = [];
    const accepted = [];

    Array.from(incoming).forEach((file) => {
      if (!isAcceptedType(file)) {
        rejected.push(`${file.name} (only photos and videos are accepted)`);
        return;
      }
      if (file.size > MAX_FILE_BYTES) {
        rejected.push(`${file.name} (over ${formatBytes(MAX_FILE_BYTES)})`);
        return;
      }
      // Deduplicate by name + size
      const duplicate = attachments.some(
        (a) => a.file.name === file.name && a.file.size === file.size,
      );
      if (duplicate) return;
      accepted.push(file);
    });

    const toAdd = accepted.slice(0, remainingSlots);
    const overflow = accepted.length - toAdd.length;

    if (toAdd.length > 0) {
      const newItems = toAdd.map((file) => ({
        file,
        url: URL.createObjectURL(file),
        kind: file.type.startsWith("video/") ? "video" : "image",
      }));
      setAttachments((prev) => [...prev, ...newItems]);
    }

    if (rejected.length > 0 || overflow > 0) {
      const parts = [];
      if (rejected.length > 0) parts.push(`Skipped: ${rejected.join(", ")}.`);
      if (overflow > 0) parts.push(`Only the first ${toAdd.length} added — max ${MAX_FILES} files total.`);
      showError(parts.join(" "));
    } else {
      setError(false);
      setErrorMsg("");
    }
  };

  const removeFile = (idx) => {
    setAttachments((prev) => {
      const next = [...prev];
      const [removed] = next.splice(idx, 1);
      if (removed) URL.revokeObjectURL(removed.url);
      return next;
    });
    setError(false);
    setErrorMsg("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.rating) {
      showError("Please tap a star rating before submitting.");
      return;
    }
    setLoading(true);
    setError(false);
    setErrorMsg("");

    try {
      const formData = new FormData();
      formData.append("form-name", "review");
      formData.append("name", form.name);
      formData.append("email", form.email);
      formData.append("rating", String(form.rating));
      formData.append("review", form.review);
      formData.append("consent", form.consent ? "yes" : "no");
      formData.append("bot-field", "");
      attachments.forEach((a) => formData.append("attachments", a.file, a.file.name));

      const response = await fetch("/", {
        method: "POST",
        body: formData, // Browser sets multipart/form-data with boundary automatically
      });
      if (!response.ok) throw new Error("Submission failed");
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      showError("Something went wrong. Please try again or email us at hello@sillybutterfly.com.");
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  return (
    <main style={{ paddingTop: "var(--nav-h)" }}>
      {/* Hero */}
      <section className="sb-noise" style={{
        position: "relative", overflow: "hidden",
        backgroundImage: `url(${heroGallery})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        padding: "clamp(60px, 10vw, 100px) 0 clamp(48px, 8vw, 80px)", textAlign: "center",
      }}>
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(160deg, rgba(58,24,68,0.75), rgba(80,30,60,0.68))",
          zIndex: 0,
        }} />
        <div className="sb-container" style={{ position: "relative", zIndex: 2 }}>
          <Reveal>
            <span style={{
              display: "inline-block", fontFamily: "var(--font-body)", fontWeight: 600,
              fontSize: "0.78rem", letterSpacing: "0.14em", textTransform: "uppercase",
              color: "var(--gold-light)", marginBottom: "12px",
            }}>
              Share the Love
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 style={{ color: "var(--white)", maxWidth: "700px", margin: "0 auto 16px" }}>
              Leave Us a <span style={{ color: "var(--gold)" }}>Review</span>
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p style={{
              color: "rgba(255,255,255,0.7)", maxWidth: "560px", margin: "0 auto",
              fontSize: "1.05rem",
            }}>
              Loved your cocktail experience? Your words help other guests find us — and they make our day, too. It only takes a minute.
            </p>
          </Reveal>
        </div>
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "80px", background: "linear-gradient(to top, var(--blush-tint), transparent)" }} />
      </section>

      {/* Form + Trust column */}
      <section className="sb-section">
        <div className="sb-container">
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 380px), 1fr))",
            gap: "clamp(32px, 5vw, 60px)",
            alignItems: "start",
          }}>
            {/* Form card */}
            <Reveal>
              <div style={{
                background: "var(--white)", borderRadius: "var(--radius-lg)",
                padding: "clamp(28px, 4vw, 40px)",
                boxShadow: "var(--shadow-md)", border: "1px solid rgba(212,168,83,0.08)",
              }}>
                {submitted ? (
                  <div style={{ textAlign: "center", padding: "32px 0" }}>
                    <span style={{ fontSize: "3rem", display: "block", marginBottom: "16px" }}>🦋</span>
                    <h3 style={{ color: "var(--plum)", marginBottom: "10px" }}>
                      Thank you — you've made our day!
                    </h3>
                    <p style={{ color: "var(--text-secondary)", marginBottom: "24px" }}>
                      Your review has been received. We read every single one, and the kind words from guests like you are what keep us shaking.
                    </p>
                    <div style={{
                      background: "var(--blush-glow)", borderRadius: "var(--radius-md)",
                      padding: "20px", textAlign: "left",
                    }}>
                      <p style={{ fontSize: "0.92rem", color: "var(--text-secondary)", marginBottom: "12px" }}>
                        Want to spread the word a little further? Tag us in your event photos:
                      </p>
                      <a
                        href="https://www.instagram.com/sillybutterflycocktails/"
                        target="_blank"
                        rel="noreferrer"
                        className="sb-btn sb-btn-outline"
                        style={{ padding: "10px 22px", fontSize: "0.88rem" }}
                      >
                        <IconInstagram /> @sillybutterflycocktails
                      </a>
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 style={{ color: "var(--plum)", marginBottom: "6px" }}>How was your cocktail experience?</h3>
                    <p style={{ color: "var(--text-secondary)", marginBottom: "24px", fontSize: "0.92rem" }}>
                      Help us improve, spread the word, and make every Silly Butterfly event even more unforgettable. 
                    </p>
                    <form
                      name="review"
                      method="POST"
                      data-netlify="true"
                      netlify-honeypot="bot-field"
                      encType="multipart/form-data"
                      onSubmit={handleSubmit}
                      style={{ display: "flex", flexDirection: "column", gap: "18px" }}
                    >
                      <input type="hidden" name="form-name" value="review" />
                      <div style={{ display: "none" }}>
                        <label>Don't fill this out: <input name="bot-field" /></label>
                      </div>

                      {/* Star rating */}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                        <span style={{
                          fontSize: "0.88rem", fontWeight: 600,
                          color: "var(--plum)", flexShrink: 0,
                        }}>
                          Your rating <span style={{ color: "var(--rose)" }}>*</span>
                        </span>
                        <StarRating
                          value={form.rating}
                          hover={hoverRating}
                          onSet={(n) => setForm((p) => ({ ...p, rating: n }))}
                          onHover={setHoverRating}
                          onLeave={() => setHoverRating(0)}
                        />
                        <input type="hidden" name="rating" value={form.rating} />
                      </div>

                      {/* Name + Event type */}
                      <div style={ROW}>
                        <div>
                          <label style={SR_ONLY} htmlFor="r-name">Your name</label>
                          <input
                            id="r-name" name="name" className="sb-input" type="text"
                            placeholder="Your name"
                            value={form.name} onChange={handleChange("name")}
                            required autoComplete="name"
                          />
                        </div>
                      </div>

                      {/* Email (optional) */}
                      <div>
                        <label style={SR_ONLY} htmlFor="r-email">Email address</label>
                        <input
                          id="r-email" name="email" className="sb-input" type="email"
                          placeholder="Your email"
                          value={form.email} onChange={handleChange("email")}
                          autoComplete="email"
                        />
                        <p style={{ fontSize: "0.78rem", color: "var(--text-light)", marginTop: "6px" }}>
                          We need your email so we can thank you and verify your review.
                        </p>
                      </div>


                      {/* Review */}
                      <div>
                        <label style={SR_ONLY} htmlFor="r-review">Your review</label>
                        <textarea
                          id="r-review" name="review" className="sb-input" rows={6}
                          placeholder="Tell us about your experience. What stood out, how your guests felt, any favourite moments…"
                          value={form.review} onChange={handleChange("review")}
                          required minLength={20} maxLength={1000}
                        />
                            </div>

                      {/* Attachments (optional) */}
                      <div>
                        <span style={{
                          display: "block", fontSize: "0.88rem", fontWeight: 600,
                          color: "var(--plum)", marginBottom: "8px",
                        }}>
                          Add photos or videos <span style={{ color: "var(--text-light)", fontWeight: 400 }}>(up to {MAX_FILES})</span>
                        </span>

                        <label
                          htmlFor={fileInputId}
                          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                          onDragLeave={() => setDragOver(false)}
                          onDrop={handleDrop}
                          style={{
                            display: "flex", flexDirection: "column", alignItems: "center",
                            justifyContent: "center", gap: "6px",
                            padding: "20px", borderRadius: "var(--radius-md)",
                            border: `1.5px dashed ${dragOver ? "var(--gold)" : "#E0D6E2"}`,
                            background: dragOver ? "rgba(212,168,83,0.06)" : "var(--blush-tint)",
                            cursor: attachments.length >= MAX_FILES ? "not-allowed" : "pointer",
                            opacity: attachments.length >= MAX_FILES ? 0.6 : 1,
                            transition: "border-color 0.2s ease, background 0.2s ease",
                            textAlign: "center",
                          }}
                        >
                          <span style={{ fontSize: "1.4rem", lineHeight: 1 }} aria-hidden="true">📸</span>
                          <span style={{ fontSize: "0.92rem", color: "var(--plum)", fontWeight: 600 }}>
                            {attachments.length >= MAX_FILES
                              ? "Maximum reached"
                              : "Tap to upload or drag files here"}
                          </span>
                          <span style={{ fontSize: "0.78rem", color: "var(--text-light)" }}>
                            Photos &amp; videos only · up to {formatBytes(MAX_FILE_BYTES)} each · {attachments.length}/{MAX_FILES} added
                          </span>
                          <input
                            ref={fileInputRef}
                            id={fileInputId}
                            name="attachments"
                            type="file"
                            accept="image/*,video/*"
                            multiple
                            disabled={attachments.length >= MAX_FILES}
                            onChange={(e) => {
                              addFiles(e.target.files);
                              // Reset so re-selecting the same file fires onChange again
                              if (fileInputRef.current) fileInputRef.current.value = "";
                            }}
                            style={SR_ONLY}
                          />
                        </label>

                        {attachments.length > 0 && (
                          <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
                            gap: "10px", marginTop: "12px",
                          }}>
                            {attachments.map((a, i) => (
                              <div
                                key={`${a.file.name}-${a.file.size}-${i}`}
                                style={{
                                  position: "relative", aspectRatio: "1/1",
                                  borderRadius: "var(--radius-sm)", overflow: "hidden",
                                  border: "1px solid rgba(212,168,83,0.2)",
                                  background: "var(--blush-glow)",
                                }}
                              >
                                {a.kind === "image" ? (
                                  <img
                                    src={a.url}
                                    alt={a.file.name}
                                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                  />
                                ) : (
                                  <video
                                    src={a.url}
                                    muted
                                    playsInline
                                    preload="metadata"
                                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                  />
                                )}
                                <span
                                  aria-hidden="true"
                                  style={{
                                    position: "absolute", left: "6px", bottom: "6px",
                                    fontSize: "0.65rem", fontWeight: 600,
                                    padding: "2px 6px", borderRadius: "99px",
                                    background: "rgba(26,15,28,0.75)", color: "var(--gold-light)",
                                    textTransform: "uppercase", letterSpacing: "0.05em",
                                  }}
                                >
                                  {a.kind}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => removeFile(i)}
                                  aria-label={`Remove ${a.file.name}`}
                                  style={{
                                    position: "absolute", top: "4px", right: "4px",
                                    width: "26px", height: "26px",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    borderRadius: "99px", border: "none",
                                    background: "rgba(26,15,28,0.8)", color: "var(--white)",
                                    cursor: "pointer", padding: 0,
                                  }}
                                >
                                  <span style={{ transform: "scale(0.55)", display: "inline-flex" }}>
                                    <IconClose />
                                  </span>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Consent */}
                      <label style={{
                        display: "flex", alignItems: "flex-start", gap: "10px",
                        fontSize: "0.88rem", color: "var(--text-secondary)", cursor: "pointer",
                      }}>
                        <input
                          type="checkbox" name="consent"
                          checked={form.consent} onChange={handleChange("consent")}
                          style={{ marginTop: "3px", accentColor: "var(--gold)" }}
                        />
                        <span>
                          I’m happy for Silly Butterfly to share my first name, last initial, review, photos, and videos on the website and social media.
                        </span>
                      </label>

                      {error && errorMsg && (
                        <p
                          role="alert"
                          style={{
                            fontSize: "0.88rem", color: "#C0392B", textAlign: "center",
                            padding: "10px 14px", background: "rgba(192,57,43,0.07)",
                            borderRadius: "var(--radius-sm)", margin: 0,
                          }}
                        >
                          {errorMsg}
                        </p>
                      )}

                      <button
                        className="sb-btn sb-btn-primary" type="submit" disabled={loading}
                        style={{ width: "100%", justifyContent: "center", opacity: loading ? 0.7 : 1 }}
                      >
                        {loading ? "Sending…" : <>Submit Review <IconArrow /></>}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </Reveal>

            {/* Trust / Why column */}
            <Reveal delay={0.15}>
              <div>
                <h3 style={{ color: "var(--plum)", marginBottom: "18px" }}>
                  Why your review matters
                </h3>
                <p style={{ color: "var(--text-secondary)", marginBottom: "28px" }}>
                  We're a small, independent team of cocktail makers — every review helps more people discover us and tells us what we're getting right (and where we can be even better).
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: "14px", marginBottom: "36px" }}>
                  {[
                    "Honest feedback only — the good and the could-be-better",
                    "Helps future guests pick the right experience",
                    "We read every single review, personally",
                    "No account, no fuss — just hit submit",
                  ].map((p, i) => (
                    <div key={i} style={{
                      display: "flex", alignItems: "flex-start", gap: "10px",
                      fontSize: "0.95rem", color: "var(--text-secondary)",
                    }}>
                      <span style={{ color: "var(--gold)", flexShrink: 0, marginTop: "2px" }}>
                        <IconCheck />
                      </span>
                      <span>{p}</span>
                    </div>
                  ))}
                </div>

                <div style={{
                  background: "var(--blush-glow)", borderRadius: "var(--radius-md)",
                  padding: "24px", border: "1px solid rgba(212,168,83,0.1)",
                }}>
                  <div style={{ display: "flex", gap: "2px", marginBottom: "12px" }}>
                    {[...Array(5)].map((_, j) => (
                      <span key={j} style={{ color: "var(--gold)" }}><IconStar /></span>
                    ))}
                  </div>
                  <p style={{
                    color: "var(--text-secondary)", fontSize: "0.95rem",
                    fontStyle: "italic", lineHeight: 1.7, marginBottom: "12px",
                  }}>
                    "Absolutely incredible experience for my hen do! The girls loved every second."
                  </p>
                  <span style={{ fontWeight: 600, color: "var(--plum)", fontSize: "0.9rem" }}>
                    — Sophie R.
                  </span>
                  <p style={{
                    fontSize: "0.82rem", color: "var(--text-light)", marginTop: "14px",
                    paddingTop: "14px", borderTop: "1px solid rgba(212,168,83,0.15)",
                  }}>
                    Join Sophie and dozens of others who've shared their Silly Butterfly story.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
